import { router, useLocalSearchParams } from 'expo-router';
import { Platform, StyleSheet, useWindowDimensions, View } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';
import Animated, {
  ScrollEvent,
  useAnimatedRef,
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useSharedValue
} from 'react-native-reanimated';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useGetMissionDetail } from '@/api/hooks/missionHooks';
import { useGetNonProfitOrgs } from '@/api/hooks/nonProfitOrgHooks';
import DetailsHeader from '@/components/DetailsHeader';
import DotsLoader from '@/components/DotsLoader';
import ListEmptyComponent from '@/components/ListEmptyComponent';
import NonProfitOrganizationCard from '@/components/npo/NonProfitOrganizationCard';
import Text from '@/components/Text';
import PlatformContainer from '@/components/web/PlatformContainer';
import { useCollapsibleHeader } from '@/hooks/useCollapsibleHeader';
import useOnLightRatingPress from '@/hooks/useOnLightRatingPress';
import { safeJsonParseArray } from '@/utils/firebaseImage';
import { i18n } from '@/utils/i18n';

const MissionDetails = () => {
  const { missionId } = useLocalSearchParams<{ missionId: string }>();
  const { data: missionDetail, isLoading, isFetching } = useGetMissionDetail(missionId);
  const { width, height } = useWindowDimensions();
  const { onLightRatingPress } = useOnLightRatingPress();

  const maxHeaderHeight = Platform.OS === 'web' ? (width >= 768 ? height * 0.65 : height * 0.4) : height * 0.5;
  const suggestionsWidth = width * 0.5 - (style.content.paddingHorizontal + style.flatListColumnWrapperStyle.gap * 0.5);
  const webSuggestionsWidth =
    width >= 1152
      ? 1152 * 0.5 - (style.content.paddingHorizontal + style.flatListColumnWrapperStyle.gap * 0.5) - 80 //Platform horizontal Padding
      : width >= 640
        ? width * 0.5 - (style.content.paddingHorizontal + style.flatListColumnWrapperStyle.gap * 0.5) - 80 //Platform horizontal Padding
        : suggestionsWidth - 32; //Platform small device horizontal Padding

  // Animation
  const animatedRef = useAnimatedRef<Animated.ScrollView>();
  const scrollOffset = useSharedValue(0);

  const { animatedHeight, progress } = useCollapsibleHeader({
    scrollOffset,
    maxHeaderHeight: maxHeaderHeight,
    minHeaderHeight: 200
  });

  const scrollHandler = useAnimatedScrollHandler((event: ScrollEvent) => {
    if (event.contentOffset.y < maxHeaderHeight) scrollOffset.value = event.contentOffset.y;
  });

  const animatedHeaderStyle = useAnimatedStyle(() => ({
    height: Math.round(animatedHeight.value),
    width: '100%',
    overflow: 'hidden'
  }));

  const animatedDotsStyle = useAnimatedStyle(() => ({
    opacity: progress.value
  }));

  // Suggestions
  const { data: charitiesResponse } = useGetNonProfitOrgs({
    radio: 300,
    type: 'charities',
    n: 0,
    limit: 4,
    claimed: true
  });

  const charitiesData = charitiesResponse?.pages.flatMap((page) => page.results);

  // ToDO: Change this when design is updated
  // This suggestions should be the closest npos to the mission and not the first four charities
  const renderSuggestions = () => (
    <FlatList
      style={style.flatList}
      contentContainerStyle={style.flatListContentContainerStyle}
      columnWrapperStyle={style.flatListColumnWrapperStyle}
      keyExtractor={(item) => item.id}
      numColumns={2}
      scrollEnabled={false}
      data={charitiesData}
      showsVerticalScrollIndicator={false}
      renderItem={({ item }) => {
        return (
          <NonProfitOrganizationCard
            npoId={item.id}
            key={item.id}
            title={item.name}
            subtitle={item.description}
            assets={safeJsonParseArray(item.thumbnail ?? '[]')}
            lightRatingValue={parseFloat(item.lightRaitingScore ?? '0')}
            claimed={item.claimed}
            onPress={() =>
              router.navigate({
                pathname: `/npoDetail/[npoId]/insights`,
                params: {
                  type: 'charity',
                  npoId: item.id,
                  thumbnail: item.thumbnail,
                  name: item.name,
                  lightRating: parseFloat(item.lightRaitingScore ?? '0')
                }
              })
            }
            lightRatingVariant={width < 768 ? 'small' : 'medium'}
            onLightRatingPress={() =>
              onLightRatingPress({ name: item.name, lightRating: parseFloat(item.lightRaitingScore ?? '0') })
            }
            size={Platform.OS === 'web' ? webSuggestionsWidth : suggestionsWidth}
            lightRatingPositon={4}
            showFavoriteButton={false}
          />
        );
      }}
      ListEmptyComponent={<ListEmptyComponent label={i18n.t('trending_detail.empty_charities')} />}
    />
  );

  return (
    <PlatformContainer>
      <SafeAreaView style={style.flexOne} edges={['bottom']}>
        <View style={style.container}>
          {isLoading || isFetching || missionDetail === undefined ? (
            <DotsLoader />
          ) : (
            <>
              {/* Header */}
              <Animated.View style={animatedHeaderStyle}>
                <DetailsHeader
                  thumbnails={missionDetail.thumbnail}
                  animatedOpacityStyle={animatedDotsStyle}
                  title={missionDetail.name}
                  itemId={missionId}
                  type="mission"
                />
              </Animated.View>
              {/* Content */}
              <Animated.ScrollView
                bounces={false}
                overScrollMode={'never'}
                showsVerticalScrollIndicator={false}
                ref={animatedRef}
                onScroll={scrollHandler}
                style={style.scrollView}
                contentContainerStyle={style.content}>
                <Text fontWeight="500" style={[style.boldText, style.subtitle]}>
                  {missionDetail.subtitle}
                </Text>
                <Text>{missionDetail.description}</Text>
                <Text fontWeight="500" style={[style.boldText, style.title, style.suggestionsTitle]}>
                  {i18n.t('mission_detail.supporting_charities')}
                </Text>
                {renderSuggestions()}
              </Animated.ScrollView>
            </>
          )}
        </View>
      </SafeAreaView>
    </PlatformContainer>
  );
};

export default MissionDetails;

const style = StyleSheet.create({
  flexOne: {
    flex: 1
  },

  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center'
  },

  scrollView: {
    width: '100%'
  },

  content: {
    flexDirection: 'column',
    paddingBottom: 25,
    paddingHorizontal: Platform.OS !== 'web' ? 18 : 0
  },

  boldText: {
    color: 'black'
  },

  title: {
    fontSize: 22
  },

  subtitle: {
    fontSize: 14,
    paddingBottom: 5
  },

  suggestionsTitle: {
    paddingTop: 25
  },

  flatList: {
    marginTop: 26
  },

  //Vertical Gap for flatList
  flatListContentContainerStyle: {
    gap: 25
  },

  //Horizontal Gap for flatList
  flatListColumnWrapperStyle: {
    gap: 22
  },

  divider: {
    marginVertical: 25
  }
});
